// WF-POLISH (Andrei, 2026-10-27: "you can add DE's but there's no way to add agents"): add an
// agent (AG) by hand — the "+" on the Workforce sidebar's Agents caption and on the Agents
// list's header open this dialog. Name, what it does, team, whom it reports to (a DE or a
// person; the caller themself first), and, optionally, where it runs (a Hermes container and
// profile). `POST …/workforce/agents` decides who may: a manager adds any; a person adds one
// that reports to them or to a DE they stand above (a refusal says why). On success the new
// agent's page opens.

import { BACKSTORY_SHAPE_HINT, profileGaps } from "@companyos/schema/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Textarea,
  toast,
  workerName,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { type CompanyAccess, isManager } from "../company";
import { problemText } from "../de/de-status";
import { myMembershipId } from "../home/home-queries";
import { keys } from "../queries";
import { useSession } from "../use-feature";
import { useAgChoices } from "./ag-actions";
import { RequestAgentButton, useAgentMode } from "./agent-requests";

/** Who sees the "+": managers, and members (the API checks the reports-to rule). */
export function mayAddAgent(company: CompanyAccess): boolean {
  return isManager(company) || company.roles.includes("member");
}

/** The "+" that opens the dialog (sidebar caption: `size="icon-xs"`; list header: labelled). */
export function AddAgentButton({
  api,
  company,
  variant,
}: {
  api: Api;
  company: CompanyAccess;
  variant: "icon" | "labelled";
}) {
  const [open, setOpen] = useState(false);
  const mode = useAgentMode(api, company);
  if (!mayAddAgent(company) || !mode.ready) return null;
  // MANAGED-POLICY: a managed company asks its agency instead.
  if (!mode.canAdd)
    return <RequestAgentButton api={api} company={company} kind="ag" variant={variant} />;
  return (
    <>
      {variant === "icon" ? (
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label="Add agent"
          title="Add agent"
          data-slot="add-agent"
          onClick={() => setOpen(true)}
        >
          <PlusIcon aria-hidden="true" />
        </Button>
      ) : (
        <Button size="sm" data-slot="add-agent" onClick={() => setOpen(true)}>
          <PlusIcon aria-hidden="true" />
          Add agent
        </Button>
      )}
      {open ? <AddAgentDialog api={api} company={company} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

export function AddAgentDialog({
  api,
  company,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { me } = useSession();
  const mine = myMembershipId(me, company);
  const choices = useAgChoices(api, company.id, true);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  // CLOCKWORK B2: role, goal and backstory are required of every agent.
  const [goal, setGoal] = useState("");
  const [backstory, setBackstory] = useState("");
  const gaps = profileGaps({ role, goal, backstory });
  const [team, setTeam] = useState("");
  const [reportsTo, setReportsTo] = useState(mine ?? "");
  const [container, setContainer] = useState("");
  const [profile, setProfile] = useState("");
  const runtimeHalf = Boolean(container.trim()) !== Boolean(profile.trim());
  const create = useMutation({
    mutationFn: () =>
      api.client.workforceAgents.create({
        params: { co: company.id },
        body: {
          name: name.trim(),
          role: role.trim(),
          goal: goal.trim(),
          backstory: backstory.trim(),
          ...(team ? { team_id: team } : {}),
          reports_to_membership_id: reportsTo,
          ...(container.trim() && profile.trim()
            ? { runtime: { container: container.trim(), profile: profile.trim() } }
            : {}),
        },
      }),
    onSuccess: async (a) => {
      await qc.invalidateQueries({ queryKey: keys.workforceAgents(company.id) });
      toast.add({ title: `${workerName(a.name)} added`, type: "success" });
      onClose();
      void navigate({
        to: "/c/$slug/agents/ag/$id",
        params: { slug: company.slug, id: a.id },
        search: keepFocus,
      });
    },
    onError: (e) => toast.add({ title: problemText(e, "The agent was not added."), type: "error" }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !reportsTo || runtimeHalf || gaps.length) return;
    create.mutate();
  };
  // The caller first (an agent most often reports to whoever adds it), then DEs, then people.
  const me1 = choices.people.find((p) => p.id === mine);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-slot="add-agent-dialog">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Add agent</DialogTitle>
            <DialogDescription>A task executor: no seat, KPIs or budget.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-name`}>Name</FieldLabel>
            <Input
              id={`${id}-name`}
              value={name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-role`}>What it does</FieldLabel>
            <Textarea
              id={`${id}-role`}
              rows={2}
              maxLength={2000}
              required
              value={role}
              onChange={(e) => setRole(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-goal`}>Goal</FieldLabel>
            <Textarea
              id={`${id}-goal`}
              rows={2}
              maxLength={4000}
              required
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
            />
            <FieldDescription>What it must achieve, and how a person judges it.</FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-backstory`}>Backstory</FieldLabel>
            <Textarea
              id={`${id}-backstory`}
              rows={5}
              maxLength={8000}
              required
              value={backstory}
              onChange={(e) => setBackstory(e.target.value)}
            />
            <FieldDescription>{BACKSTORY_SHAPE_HINT}</FieldDescription>
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor={`${id}-to`}>Reports to</FieldLabel>
              <NativeSelect
                id={`${id}-to`}
                value={reportsTo}
                required
                onChange={(e) => setReportsTo(e.target.value)}
              >
                <NativeSelectOption value="" disabled>
                  {choices.status === "loading" ? "Loading…" : "Pick a DE or a person"}
                </NativeSelectOption>
                {mine ? (
                  <NativeSelectOption value={mine}>
                    {me1 ? `Me (${me1.name})` : "Me"}
                  </NativeSelectOption>
                ) : null}
                {choices.des.map((d) => (
                  <NativeSelectOption key={d.id} value={d.id}>
                    DE · {workerName(d.name)}
                  </NativeSelectOption>
                ))}
                {choices.people
                  .filter((p) => p.id !== mine)
                  .map((p) => (
                    <NativeSelectOption key={p.id} value={p.id}>
                      {p.name}
                    </NativeSelectOption>
                  ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor={`${id}-team`}>Team</FieldLabel>
              <NativeSelect
                id={`${id}-team`}
                value={team}
                onChange={(e) => setTeam(e.target.value)}
              >
                <NativeSelectOption value="">No team</NativeSelectOption>
                {choices.teams.map((t) => (
                  <NativeSelectOption key={t.id} value={t.id}>
                    {t.name}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          </div>
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
              Where it runs (optional)
              <InfoTip label="About where it runs">
                Its Hermes container and profile, when it already runs somewhere. Both or neither.
              </InfoTip>
            </legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor={`${id}-container`}>Container</FieldLabel>
                <Input
                  id={`${id}-container`}
                  value={container}
                  maxLength={256}
                  onChange={(e) => setContainer(e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor={`${id}-profile`}>Profile</FieldLabel>
                <Input
                  id={`${id}-profile`}
                  value={profile}
                  maxLength={256}
                  onChange={(e) => setProfile(e.target.value)}
                />
              </Field>
            </div>
            {runtimeHalf ? (
              <p className="text-xs text-danger-text" role="alert">
                Give both the container and the profile, or neither.
              </p>
            ) : null}
          </fieldset>
          {choices.status === "error" ? (
            <p className="text-xs text-danger-text" role="alert">
              The choices could not be loaded.{" "}
              <button type="button" className="underline" onClick={choices.retry}>
                Retry
              </button>
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !name.trim() || !reportsTo || runtimeHalf || gaps.length > 0 || create.isPending
              }
            >
              {create.isPending ? "Adding…" : "Add agent"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
